import {Middleware} from '@reduxjs/toolkit'
import {parse, stringify, IStringifyOptions} from 'qs'
import {omit, pipe, flip, partial} from 'ramda'

import {safelySetStorageItem} from '../../../utils/persistence'
import {getLastLoginUrl} from '../../business/persistData'
import {addSSOTrigger} from '../../business/authentication'
import {
  TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY,
  setAudienceTokenInBrowser
} from '../actions'

import {parseUserTokenFromCallbackUrl} from '../thunks'

const MAGIC_LOGIN_CODE_URL_PARAM = 'o'
const MAGIC_LOGIN_PHONE_URL_PARAM = 'i'

/**
 * Cleans up an url from all the magic login attributes
 * @param url The url to be cleaned
 * @returns the cleaned up url
 */
export const cleanMagicLoginParamsFromUrl = (url: string) => {
  /* This conditional was added due to specifics of `parse` and `stringify` methods
   * If URL does not contain any GET parameters then `parse` will convert it to object:
   * URL: http://findhotel.net/user/membership
   * `parse`: { 'http://findhotel.net/user/membership' : '' }
   * `stringify`: http://findhotel.net/user/membership=
   * Stringify method takes object keys one by one and appends keys to them with equals sign
   *
   * So if URL doesn't contain search part we just need to return url without transformations
   * */
  if (!url.includes('?')) return url

  const flippedStringify = flip<
    IStringifyOptions,
    Record<string, unknown>,
    string
  >(stringify)

  const partialStringify = partial(flippedStringify, [
    {
      encodeValuesOnly: true
    }
  ])

  return pipe<
    [string],
    Record<string, unknown>,
    Record<string, unknown>,
    string
  >(
    parse,
    omit([MAGIC_LOGIN_CODE_URL_PARAM, MAGIC_LOGIN_PHONE_URL_PARAM]),
    partialStringify
  )(url)
}

/**
 * Middleware to get token from user url  and set necessary localStorage data.
 */
// eslint-disable-next-line unicorn/consistent-function-scoping
const middleware: Middleware = () => next => action => {
  next(action)

  switch (action.type) {
    case parseUserTokenFromCallbackUrl.fulfilled.type: {
      const lastLoginUrl = getLastLoginUrl()

      setTimeout(() => {
        window.location.href = lastLoginUrl
          ? addSSOTrigger(cleanMagicLoginParamsFromUrl(lastLoginUrl))
          : addSSOTrigger(window.location.origin)
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)

      break
    }

    case parseUserTokenFromCallbackUrl.rejected.type: {
      window.history.replaceState(
        {},
        document.title,
        cleanMagicLoginParamsFromUrl(window.location.href)
      )
      break
    }

    case setAudienceTokenInBrowser.type: {
      safelySetStorageItem(
        localStorage,
        'audienceToken',
        action.payload,
        'audienceToken failed'
      )

      break
    }

    default:
      break
  }
}

export default middleware
